import { Component } from 'react';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import logger from '../services/logger';
import { isChunkLoadError, reloadForStaleChunk } from '../utils/staleChunkRecovery';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isReloadingForUpdate: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Stale deployment assets (Issue #906): reload once onto the current release instead of
    // surfacing an error. If a reload was already attempted, fall through and log normally.
    if (isChunkLoadError(error) && reloadForStaleChunk()) {
      this.setState({ isReloadingForUpdate: true });
      return;
    }

    logger.error('Unhandled React Render Error', error, {
      componentStack: errorInfo?.componentStack,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.isReloadingForUpdate) {
      return (
        <Box
          role="status"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 2,
            px: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Updating to the latest version…
          </Typography>
        </Box>
      );
    }

    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            px: 2,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 500,
              textAlign: 'center',
              borderRadius: 2,
            }}
          >
            <Typography variant="h5" color="error" fontWeight="bold" gutterBottom>
              Something Went Wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              An unexpected application error occurred. Our team has been notified.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReload}
              sx={{ px: 4, py: 1 }}
            >
              Reload Page
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
